<template>
  <div class="event-page-wrapper">
    <!-- Loader Component -->
    <Loader v-if="isLoading" />

    <!-- Main Content -->
    <div v-else :class="['event-inner', { 'blur-content': isBlurred }]">
      <!-- Top Bar -->
      <div v-if="topBarComponents.length" class="top-bar">
        <component
          v-for="(component, index) in topBarComponents"
          :is="component"
          :key="index"
          :event="event"
          :attendee="attendee"
          :isSignedUp="isSignedUp"
          :isPayable="isPayable"
          :hasLeftSidebar="hasLeftSidebar"
          :hasRightSidebar="hasRightSidebar"
          :isLeftSidebarOpen="isLeftSidebarOpen"
          :isSidebarOpen="isSidebarOpen"
          :isRightSidebarOpen="isRightSidebarOpen"
          :eventType="eventType"
          @toggle-left-sidebar="toggleLeftSidebar"
          @toggle-sidebar="toggleSidebar"
          @toggle-right-sidebar="toggleRightSidebar"
        />
      </div>

      <div class="content-area">
        <!-- Left Sidebar -->
        <div
          v-if="showLeftSidebar"
          :class="['left-sidebar space-y-4', { 'is-open': isLeftSidebarOpen }]"
        >
          <component
            v-for="(component, index) in leftSidebarComponents"
            :is="component"
            :key="index"
            :event="event"
            :attendee="attendee"
            :isSignedUp="isSignedUp"
            :eventId="eventId"
            :isPayable="isPayable"
            @join-event="joinEvent"
            @pay-event="payEvent"
            @no-content="() => handleLeftNoContent(index)"
          />
        </div>

        <!-- Main Content Area -->
        <div class="main-content">
          <component
            v-for="(component, index) in mainComponents"
            :is="component"
            :key="index"
            :event="event"
            :getColor="getColor"
            :attendee="attendee"
            :isSignedUp="isSignedUp"
            :eventId="eventId"
            :isPayable="isPayable"
            :isSidebarOpen="isSidebarOpen"
            :isRightSidebarOpen="isRightSidebarOpen"
            :eventType="eventType"
            :btnJoinText="computedBtnJoinText"
            @toggle-right-sidebar="toggleRightSidebar"
            @join-event="joinEvent"
            @pay-event="payEvent"
            @no-content="() => handleLeftNoContent(index)"
            @open-share-modal="openShareModal"
          />
        </div>

        <!-- Right Sidebar -->
        <div
          v-if="showRightSidebar"
          :class="[
            'right-sidebar space-y-4',
            { 'is-open': isRightSidebarOpen },
          ]"
        >
          <button
            v-if="isRightSidebarOpen && !isLargeScreen"
            class="w-full flex justify-end items-center pr-4"
            @click="toggleRightSidebar"
          >
            <div
              class="close-button flex items-center justify-center text-red-500 font-bold py-3 px-4 rounded-lg transition-transform duration-300 ease-in-out hover:rotate-90 hover:scale-110"
            >
              <font-awesome-icon icon="times" class="h-10 w-10" />
            </div>
          </button>

          <component
            v-for="(component, index) in rightSidebarComponents"
            :is="component"
            :key="index"
            :event="event"
            :attendee="attendee"
            :isSignedUp="isSignedUp"
            :eventId="eventId"
            :isPayable="isPayable"
            :getColor="getColor"
            :eventType="eventType"
            :btnJoinText="computedBtnJoinText"
            @join-event="joinEvent"
            @pay-event="payEvent"
            @no-content="() => handleRightNoContent(index)"
          />
        </div>
      </div>

      <!-- Bottom Bar -->
      <div v-if="bottomBarComponents.length" class="bottom-bar">
        <component
          v-for="(component, index) in bottomBarComponents"
          :is="component"
          :key="index"
          :event="event"
          :attendee="attendee"
          :isSignedUp="isSignedUp"
          :eventId="eventId"
          :isPayable="isPayable"
        />
      </div>
    </div>
    <ShareModal
      v-if="isEventReady"
      :isOpen="isShareModalOpen"
      :eventUrl="eventUrl"
      :event="event"
      :isSignedUp="isSignedUp"
      :vAFh="vAFh"
      :eventId="eventId"
      @close="closeShareModal"
    />

    <ReviewModal ref="reviewModal" />
    <!-- FormModal for showing form when triggered -->
    <FormModal
      v-if="isFormModalOpen"
      :isOpen="isFormModalOpen"
      :formId="formId"
      @close="closeFormModal"
    />
    <PasswordModal
      v-if="restrictionType === 'password' && !isRestrictionPassed"
      :isOpen="isPasswordModalOpen"
      @submit-password="submitPassword"
    />
    <AccountConversionModal
      v-if="isAccountConversionModalOpen"
      @close="closeAccountConversionModal"
    />
    <TagRestrictionModal
      v-if="isTagRestrictionModalOpen"
      :message="tagRestrictionMessage"
    />
    <!-- Mobile Sidebar for Right Sidebar -->
    <MobileSidebar
      :isSidebarOpen="isSidebarOpen"
      @close-sidebar="toggleSidebar"
      :eventType="eventType"
    />
    <MissingInfoModal v-if="isMissingInfoModalOpen" />
  </div>
  <!--<ChatWrapper />-->
</template>

<script>
import { ref, shallowRef, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { moduleConfig } from "@/utils/moduleConfig";
import { eventDetails } from "@/composables/eventDetails";
import { initializeSocket } from "@/utils/socket";
import {
  getSystemPath,
  getEventType,
  getColorClass,
  btnJoinText,
} from "@/utils/helper";
import { initPushSw } from "@/utils/pushNotifications";
import { showNotification } from "@/utils/notificationUtils";
import { translate } from "@/utils/translate";
import Loader from "@/components/general/ApiLoader.vue";
import FormModal from "@/components/modals/FormModal.vue";
import ShareModal from "@/components/modals/ShareModal.vue";
import ReviewModal from "@/components/modals/ReviewModal.vue";
import PasswordModal from "@/components/modals/PasswordModal.vue";
import TagRestrictionModal from "@/components/modals/TagRestrictionModal.vue";
import MissingInfoModal from "@/components/modals/MissingInfoModal.vue";
import AccountConversionModal from "@/components/modals/AccountConversionModal.vue"; // Import the new modal
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { getUserData, cookie, getCookieValue, setCookie } from "@/utils/cookie";
import { notify } from "notiwind";
import { sendUserAction, closeSocket } from "@/utils/socket";
import MobileSidebar from "../general/MobileSidebar.vue";

//import ChatWrapper from '@/components/default/module/chat/ChatWrapper.vue'; // Adjust the path as necessary

export default {
  name: "EventPage",
  props: {
    type: {
      type: String,
      required: true, // Mark as required if it's always expected
    },
    id: {
      type: String,
      required: true,
    },
  },
  components: {
    ShareModal,
    ReviewModal,
    FormModal,
    FontAwesomeIcon,
    PasswordModal,
    TagRestrictionModal,
    MobileSidebar,
    MissingInfoModal,
    AccountConversionModal,
    btnJoinText,
    Loader,
    // ChatWrapper,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const eventSlug = ref(route.params.id);
    const isLoading = ref(true); // Initialize loading state
    const {
      event,
      attendee,
      fetchEventDetails,
      isSignedUp,
      vAFh,
      eventId,
      isPayable,
    } = eventDetails(eventSlug.value);
    const eventType = computed(() =>
      getEventType(event.value?.type?.type || "default")
    );

    const isFormModalOpen = ref(false); // Track if FormModal is open
    const formId = ref(null); // Store the form data for FormModal

    const restrictionType = ref(null);
    const isRestrictionPassed = ref(false);
    const isPasswordModalOpen = ref(false);
    const isBlurred = computed(
      () =>
        (restrictionType.value && !isRestrictionPassed.value) ||
        isAccountConversionModalOpen.value
    );
    const isAccountConversionModalOpen = ref(false);
    const isTagRestrictionModalOpen = ref(false); // Track modal visibility
    const isMissingInfoModalOpen = ref(false);
    const tagRestrictionMessage = ref(""); // Store restriction message

    const isLargeScreen = ref(false);

    const handleResize = () => {
      isLargeScreen.value = window.innerWidth >= 1122;
    };

    /*
        const leftSidebarComponents = ref([]);
        const rightSidebarComponents = ref([]);
        const topBarComponents = ref([]);
        const bottomBarComponents = ref([]);
        const mainComponents = ref([]);
    */
    const leftSidebarComponents = shallowRef([]);
    const rightSidebarComponents = shallowRef([]);
    const topBarComponents = shallowRef([]);
    const bottomBarComponents = shallowRef([]);
    const mainComponents = shallowRef([]);

    const isLeftSidebarOpen = ref(false);
    const isRightSidebarOpen = ref(false);
    const isSidebarOpen = ref(false);

    // Track no-content emits
    //const leftNoContentCount = ref(0);
    //const rightNoContentCount = ref(0);
    const leftComponentCount = ref(0);
    const rightComponentCount = ref(0);

    const hasLeftSidebar = ref(false); // New flag for left sidebar visibility
    const hasRightSidebar = ref(false); // New flag for right sidebar visibility

    const showLeftSidebar = ref(false); // New flag for left sidebar presence

    const leftNoContentStatus = ref([]); // Array to track no-content for left sidebar components
    const rightNoContentStatus = ref([]); // Array to track no-content for right sidebar components

    const toggleLeftSidebar = () => {
      isLeftSidebarOpen.value = !isLeftSidebarOpen.value;
    };

    const toggleRightSidebar = () => {
      isRightSidebarOpen.value = !isRightSidebarOpen.value;
    };

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    const showRightSidebar = () => {
      this.isRightSidebarOpen = true;
    };
    const shouldShowConversionModal = () => {
      const dismissed = getCookieValue("conversion_modal_dismissed");
      return !dismissed;
    };
    const checkUserInformation = () => {
      const user = getUserData();
      if (user) {
        if (
          user.type === "partner" &&
          (!user.first_name || user.first_name.trim() === "") &&
          (!user.last_name || user.last_name.trim() === "")
        ) {
          isMissingInfoModalOpen.value = true;
          isBlurred.value = true; // Blur the content if user information is incomplete
        } else if (user.type === "crm" && shouldShowConversionModal()) {
          isAccountConversionModalOpen.value = true;
          isBlurred.value = true; // Blur the content if account conversion is required
        }
      }
    };

    const getComponentsForLocation = async (location) => {
      // Fallback to default mode if event mode type is missing or undefined
      const defaultModeConfig = moduleConfig.default;
      const modeConfig =
        moduleConfig[event.value?.mode?.type] || defaultModeConfig;

      // Fallback to default status if event status is missing or undefined
      const eventStatus = event.value?.status;
      const defaultStatusConfig =
        defaultModeConfig[eventStatus] || defaultModeConfig.upcoming;
      const statusConfig = modeConfig[eventStatus] || defaultStatusConfig;

      // Determine if the user is signed up or not, and fallback to the default 'isNotSignedUp' or 'isSignedUp'
      let signUpConfig = statusConfig.isNotSignedUp; // Default to 'isNotSignedUp'
      if (isSignedUp.value && !isPayable.value) {
        signUpConfig =
          statusConfig.isSignedUp ||
          defaultStatusConfig.isSignedUp ||
          defaultStatusConfig.isNotSignedUp;
      }

      // Fetch components for the given location (e.g., 'leftSidebar', 'mainContent', etc.)
      const components =
        signUpConfig?.[location] ||
        defaultStatusConfig.isNotSignedUp?.[location];

      // Ensure components is an array, and load components asynchronously
      if (Array.isArray(components)) {
        const loadedComponents = [];
        for (const comp of components) {
          try {
            const resolvedComponent = await comp();
            if (resolvedComponent) {
              loadedComponents.push(resolvedComponent);
            }
          } catch (error) {
            console.error(`Error loading component for ${location}:`, error);
          }
        }
        return loadedComponents;
      } else {
        return [];
      }
    };

    const loadComponents = async () => {
      leftSidebarComponents.value = await getComponentsForLocation(
        "leftSidebar"
      );
      rightSidebarComponents.value = await getComponentsForLocation(
        "rightSidebar"
      );
      topBarComponents.value = await getComponentsForLocation("topBar");
      bottomBarComponents.value = await getComponentsForLocation("bottomBar");
      mainComponents.value = await getComponentsForLocation("mainContent");
      // Reset no-content status array with the same length as components
      leftNoContentStatus.value = Array(
        leftSidebarComponents.value.length
      ).fill(false);
      rightNoContentStatus.value = Array(
        rightSidebarComponents.value.length
      ).fill(false);

      // Update visibility based on components and content status
      leftComponentCount.value = leftSidebarComponents.value.length;
      rightComponentCount.value = rightSidebarComponents.value.length;

      hasLeftSidebar.value = leftComponentCount.value > 0;
      hasRightSidebar.value = rightComponentCount.value > 0;
      updateLeftSidebarVisibility();
      updateRightSidebarVisibility();
    };

    const updateLeftSidebarVisibility = () => {
      // Show sidebar only if at least one component has content
      showLeftSidebar.value = leftNoContentStatus.value.some(
        (status) => status === false
      );
    };

    const updateRightSidebarVisibility = () => {
      // Show sidebar only if at least one component has content
      showRightSidebar.value = rightNoContentStatus.value.some(
        (status) => status === false
      );
    };

    // Define computed properties to track sidebar visibility
    /*  const hasLeftSidebar = computed(() => leftComponentCount.value > 0);
      const hasRightSidebar = computed(() => rightComponentCount.value > 0);
   
      const showLeftSidebar = computed(() => leftComponentCount.value > leftNoContentCount.value);
      const showRightSidebar = computed(() => rightComponentCount.value > rightNoContentCount.value);
  */
    const handleLeftNoContent = (index) => {
      leftNoContentStatus.value[index] = true;
      updateLeftSidebarVisibility(); // Re-evaluate whether the sidebar should be shown
    };
    const getColor = (shade = 500, prefix = "text") => {
      return getColorClass(eventType.value, shade, prefix);
    };
    const computedBtnJoinText = computed(() =>
      btnJoinText(event.value.type.type, event.value.status)
    );
    const handleRightNoContent = (index) => {
      rightNoContentStatus.value[index] = true;
      updateRightSidebarVisibility(); // Re-evaluate whether the sidebar should be shown
    };

    const isShareModalOpen = ref(false);
    const reviewModal = ref(null); // Create a ref for ReviewModal
    const eventUrl = computed(() => window.location.href);
    const isEventReady = computed(() => event.value && event.value.title);
    const joinEvent = () => {
      const joinPath = getSystemPath("join");
      const fullPath = `/${route.params.type}/${route.params.id}/${joinPath}`;
      router.push(fullPath);
    };

    const payEvent = () => {
      const payPath = getSystemPath("pay");
      const fullPath = `/${route.params.type}/${route.params.id}/${payPath}`;
      router.push(fullPath);
    };

    const openShareModal = () => {
      sendUserAction("open", "share");
      isShareModalOpen.value = true;
    };
    const openFormModal = (id) => {
      sendUserAction("open", "form");
      formId.value = id;
      isFormModalOpen.value = true;
    };
    const closeFormModal = () => {
      sendUserAction("close", "form");
      formId.value = null;
      isFormModalOpen.value = false;
    };
    const closeAccountConversionModal = () => {
      isAccountConversionModalOpen.value = false;
      isBlurred.value = false;
      // Set a cookie to prevent the modal from showing again for one day
      setCookie("conversion_modal_dismissed", "true", {
        "max-age": 24 * 60 * 60,
      });
    };
    const closeShareModal = () => {
      sendUserAction("close", "share");
      isShareModalOpen.value = false;
    };

    const openReviewModal = (eventId) => {
      reviewModal.value.openModal(eventId);
    };

    onMounted(async () => {
      checkUserInformation();
      await fetchEventDetails();
      await loadComponents();
      if (
        isSignedUp.value &&
        event.value &&
        event.value.id &&
        !localStorage.getItem(`autoShareModalOpened_${event.value.id}`)
      ) {
        setTimeout(() => {
          openShareModal();
          localStorage.setItem(
            `autoShareModalOpened_${event.value.id}`,
            "true"
          );
        }, 15000); // 30000ms = 30 seconds
      }
      if (isSignedUp.value && !isPayable.value) {
        initPushSw(); // Initialize the service worker and push notifications
      }

      if (event.value && event.value.id) {
        isLoading.value = false;
        restrictionType.value = event.value.restriction || false;

        const restrictionKey = `event_${eventId.value.id}_restriction_passed`;
        const restrictionPassed = localStorage.getItem(restrictionKey);

        if (restrictionPassed) {
          isRestrictionPassed.value = true;
        }

        if (restrictionType.value && !isRestrictionPassed.value) {
          if (restrictionType.value === "password") {
            isPasswordModalOpen.value = true;
          } else if (restrictionType.value === "tags") {
            const user = getUserData();
            if (user) {
              await checkTagRestriction();
            } else {
              router.push({
                path: `/${getSystemPath("login")}`,
                query: { redirect: route.fullPath },
              });
            }
          }
        }

        initializeSocket(event.value.id, async (message) => {
          if (
            message.type === "liveCountUpdate" &&
            message.event_id === event.value.id
          ) {
            if (event.value && event.value.impression) {
              event.value.impression.live = message.data.count;
            }
          } else if (message.type === "payment_confirmation") {
            notify(
              {
                title: translate("Success"),
                text: translate("Payment has been received successfully"),
                type: "info",
                group: "info",
              },
              15000
            );

            // Refresh the event details and components
            await fetchEventDetails();
            await loadComponents();
          } else if (
            message.type === "update" &&
            message.event_id === event.value.id
          ) {
            let hasChanges = false; // Track if any changes are made

            if (message.data?.set) {
              // update action to overwrite
              message.data.set.forEach((update) => {
                for (const key in update) {
                  if (
                    Object.prototype.hasOwnProperty.call(update, key) &&
                    Object.prototype.hasOwnProperty.call(event.value, key)
                  ) {
                    // Check if the new value differs from the current value
                    if (event.value[key] !== update[key]) {
                      event.value[key] = update[key];
                      hasChanges = true; // Mark as changed
                    }
                  }
                }
              });

              // Only load components if something has changed
              if (hasChanges) {
                await loadComponents();
              }
            } else {
              // If there's no set data, reload components as usual
              await fetchEventDetails();
              await loadComponents();
            }
          } else if (
            message.type === "notification" &&
            message.event_id === event.value.id
          ) {
            showNotification(message.data);
          } else if (message.type === "action") {
            switch (message.data.type) {
              case "share":
                isShareModalOpen.value = true; // Open ShareModal
                break;
              case "form":
                openFormModal(message.data.form_id);
                break;
              case "review":
                openReviewModal(event.value.id); // Open ReviewModal
                break;

              default:
                console.log("Unknown action type:", message.data.type);
            }
          } else if (message.type === "userJoined") {
            // Notify about user joined
            //    console.log("User joined:", message.data.user);
            notify({
              group: "userActivity",
              avatar: message.data.user.avatar,
              message: translate("%s has joined %s", [
                message.data.user.name,
                event.value.type.vocative.toLowerCase(),
              ]),
            });
          } else if (message.type === "userLeft") {
            // Notify about user left
            notify({
              group: "userActivity",
              avatar: message.data.user.avatar,
              message: translate("%s has left %s", [
                message.data.user.name,
                event.value.type.vocative.toLowerCase(),
              ]),
            });
          } else if (message.type === "modaratorMessage") {
            // Assuming moderators receive this message
            notify(
              {
                group: "moderatorActivity",
                type: "info",
                title: translate("Moderator Notification"),
                text: message.data.message, // The message like "Total live users: X"
                image: message.data.image ?? false, // The image URL
              },
              5000
            );
          }
        });
      }

      handleResize(); // Initialize
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      closeSocket();
      window.removeEventListener("resize", handleResize);
    });

    const checkTagRestriction = async () => {
      try {
        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/check-tag-restriction`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              event_id: eventId.value,
              session_id: cookie(),
              brand_id: window.vueAppConfig.info.id,
            }),
          }
        );

        const data = await response.json();
        if (data.status) {
          isRestrictionPassed.value = true;
          const restrictionKey = `event_${eventId.value.id}_restriction_passed`;
          localStorage.setItem(restrictionKey, "true");
          await loadComponents();
        } else if (data.modal) {
          // Show modal if data contains a modal field
          tagRestrictionMessage.value = data.message;
          isTagRestrictionModalOpen.value = true; // Open the tag restriction modal
        } else {
          notify(
            {
              group: "error",
              title: translate("Error"),
              text: data.message,
            },
            15000
          );
        }
      } catch (error) {
        console.error("Error checking tag restriction:", error);
      }
    };

    const submitPassword = async (password) => {
      try {
        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/check-password`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              event_id: eventId.value,
              password: password,
              session_id: cookie(),
              brand_id: window.vueAppConfig.info.id,
            }),
          }
        );

        const data = await response.json();
        if (data.status) {
          isRestrictionPassed.value = true;
          const restrictionKey = `event_${eventId.value.id}_restriction_passed`;
          localStorage.setItem(restrictionKey, "true");
          isPasswordModalOpen.value = false;
          await loadComponents();
        } else {
          notify(
            {
              group: "error",
              title: translate("Error"),
              text: data.message,
            },
            15000
          );
        }
      } catch (error) {
        console.error("Error checking password restriction:", error);
      }
    };

    return {
      event,
      isLoading,
      attendee,
      leftSidebarComponents,
      rightSidebarComponents,
      topBarComponents,
      bottomBarComponents,
      mainComponents,
      isLeftSidebarOpen,
      isRightSidebarOpen,
      hasLeftSidebar,
      hasRightSidebar,
      showLeftSidebar,

      isShareModalOpen,
      eventUrl,
      isEventReady,
      reviewModal,
      vAFh,
      eventId,
      isSignedUp,
      isPayable,
      joinEvent,
      payEvent,
      openShareModal,
      closeShareModal,
      toggleLeftSidebar,
      toggleRightSidebar,
      handleLeftNoContent,
      handleRightNoContent,
      isFormModalOpen, // form
      formId, //  form
      closeFormModal, // form
      showRightSidebar,

      isBlurred, // restriction
      restrictionType, // restriction
      isRestrictionPassed, // restriction
      isPasswordModalOpen, // restriction
      submitPassword, // restriction
      isTagRestrictionModalOpen, // restriction
      tagRestrictionMessage, // restriction
      eventType,
      toggleSidebar,
      isSidebarOpen,
      isLargeScreen,
      getColor,
      isMissingInfoModalOpen,

      isAccountConversionModalOpen, // account conversion
      closeAccountConversionModal, // account conversion

      computedBtnJoinText, // cta
    };
  },
};
</script>

<style scoped>
.event-page-wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
  background-color: #fff;
}

.event-inner {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.top-bar,
.bottom-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.content-area {
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  flex-direction: column;
}

.left-sidebar,
.right-sidebar {
  flex-shrink: 0;
  overflow-y: auto;
  background-color: #ebebeb;
  flex-basis: 30rem;
  /* Set a default size */
  max-width: 100%;
  /* Prevent the sidebar from growing beyond the viewport width */
  transition: width 0.3s ease;
  /* Smooth transition */
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.main-content {
  flex-grow: 1;
  overflow-y: auto;
  min-width: 0;
  /* Ensures content does not overflow */
}

.blur-content {
  filter: blur(5px);
  pointer-events: none;
}

@media (min-width: 1122px) {
  .content-area {
    flex-direction: row;
  }

  .left-sidebar,
  .right-sidebar {
    width: 30rem;
    /* Define a specific width for sidebars on larger screens */
    flex-basis: 30rem;
    /* Sidebar takes up the specified space */
    max-width: 30%;
    /* Limit the maximum width to 25% of the container */
  }
}

@media (max-width: 1122px) {
  .left-sidebar,
  .right-sidebar {
    display: none;
    /* Hide sidebars on smaller screens */
    position: absolute;
    /* Position fixed for mobile to overlay */
    top: 0;
    bottom: 0;
    width: 80%;
    /* Sidebar takes up most of the screen width on mobile */
    max-width: 80%;
    /* Ensure it does not exceed the max-width */
    z-index: 10;
    /* Ensure the sidebar appears above the main content */
    background-color: #f4f4f3;
  }

  .left-sidebar.is-open,
  .right-sidebar.is-open {
    display: block;
    /* Display sidebars when they are open */
  }
}
</style>
